// Set-password landing for invite + password-recovery email links.
// GoTrue redirects the email link here with the session in the URL hash
// (implicit flow); the supabase browser client auto-detects it (detectSessionInUrl
// defaults on) and fires a PASSWORD_RECOVERY / SIGNED_IN event. Once a session
// exists the user chooses a new password and is signed in.
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { supabase } from '@/lib/supabase';
import { PRODUCT_NAME } from '@/lib/brand';

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<'checking' | 'ready' | 'invalid'>('checking');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  // MFA step-up: accounts with a verified TOTP factor establish this recovery
  // session at aal1, but GoTrue rejects updateUser({ password }) below aal2 with
  // "AAL2 session is required". When we detect that, require a TOTP code and
  // step the session up to aal2 BEFORE updating the password (mirror of tprm7 #80).
  const [needsMfa, setNeedsMfa] = useState(false);
  const [mfaCode, setMfaCode] = useState('');

  useEffect(() => {
    let settled = false;
    const settleReady = async () => {
      if (settled) return;
      settled = true;
      // Probe assurance level now that a session exists. currentLevel !== 'aal2'
      // with nextLevel === 'aal2' means "has a verified factor but this session
      // is only aal1" → we must challenge before the password update.
      try {
        const { data } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
        if (data && data.currentLevel !== 'aal2' && data.nextLevel === 'aal2') {
          setNeedsMfa(true);
        }
      } catch {
        // Non-fatal: if the probe fails we proceed; a genuine AAL rejection will
        // still surface from updateUser rather than dead-ending the user here.
      }
      setStatus('ready');
    };

    // If detectSessionInUrl already consumed the hash before this mounted, the
    // session is present immediately.
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) settleReady();
    });

    // Otherwise wait for the auth event the hash consumption fires.
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) settleReady();
    });

    // Decide "invalid" only after giving the async hash-exchange a moment.
    const timer = setTimeout(async () => {
      if (settled) return;
      const { data } = await supabase.auth.getSession();
      if (data.session) {
        settleReady();
      } else {
        settled = true;
        setStatus('invalid');
      }
    }, 2000);

    return () => {
      sub.subscription.unsubscribe();
      clearTimeout(timer);
    };
  }, []);

  // Step the recovery session up to aal2 with a TOTP code. Reuses MfaGate's
  // factor-selection (the verified TOTP factor is fl.totp[0]). Throws a friendly
  // message on a bad code so the caller can offer a retry rather than a dead end.
  const stepUpMfa = async (code: string) => {
    const { data: fl, error: fErr } = await supabase.auth.mfa.listFactors();
    if (fErr) throw fErr;
    const factorId = fl?.totp?.[0]?.id ?? null;
    if (!factorId) throw new Error('No authenticator is set up for this account.');
    const { data: ch, error: chErr } = await supabase.auth.mfa.challenge({ factorId });
    if (chErr) throw chErr;
    const { error: vErr } = await supabase.auth.mfa.verify({ factorId, challengeId: ch.id, code });
    if (vErr) throw new Error('That code didn’t match. Check your authenticator app and try again.');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (password.length < 12) {
      setError('Password must be at least 12 characters.');
      return;
    }
    if (password !== confirm) {
      setError('The two passwords do not match.');
      return;
    }
    if (needsMfa && mfaCode.trim().length < 6) {
      setError('Enter your 6-digit authenticator code to continue.');
      return;
    }
    setSaving(true);
    try {
      // For an MFA account, step up to aal2 first — otherwise GoTrue rejects the
      // password update with "AAL2 session is required".
      if (needsMfa) {
        await stepUpMfa(mfaCode.trim());
      }
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      // Signed in with the new password → land in the app (MFA gate handles step-up).
      navigate('/', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not set your password. Try the link again.');
      // Clear the code so a wrong-code retry starts clean (not a dead end).
      if (needsMfa) setMfaCode('');
    } finally {
      setSaving(false);
    }
  };

  const card: React.CSSProperties = {
    width: '400px', background: 'var(--surface)', borderRadius: '16px', padding: '40px',
    boxShadow: 'var(--shadow-lg)', border: '1px solid var(--border)',
  };
  const input: React.CSSProperties = {
    width: '100%', padding: '10px 12px', borderRadius: '8px',
    border: '1px solid var(--border)', fontSize: '14px', outline: 'none',
  };

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'var(--bg-secondary)',
    }}>
      <div style={card}>
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{
            width: '48px', height: '48px', background: 'var(--accent)', borderRadius: '12px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'white', fontSize: '20px', fontWeight: 700, margin: '0 auto 12px',
          }}>{PRODUCT_NAME.slice(0, 2).toUpperCase()}</div>
          <h1 style={{ fontSize: '22px', fontWeight: 700 }}>Set your password</h1>
          <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginTop: '4px' }}>
            Choose a password to finish setting up your {PRODUCT_NAME} account.
          </p>
        </div>

        {status === 'checking' && (
          <p style={{ textAlign: 'center', fontSize: '13px', color: 'var(--text-secondary)' }}>
            Verifying your link…
          </p>
        )}

        {status === 'invalid' && (
          <div style={{ textAlign: 'center' }}>
            <div style={{
              padding: '10px 12px', borderRadius: '6px', background: '#FEE2E2',
              color: '#DC2626', fontSize: '13px', marginBottom: '16px',
            }}>
              This link is invalid or has expired.
            </div>
            <a href="/login" style={{ fontSize: '13px', color: 'var(--accent)', fontWeight: 600 }}>
              Back to sign in
            </a>
          </div>
        )}

        {status === 'ready' && (
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, marginBottom: '6px' }}>
                New password
              </label>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 12 characters" autoComplete="new-password" required style={input} />
            </div>
            <div style={{ marginBottom: needsMfa ? '16px' : '24px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, marginBottom: '6px' }}>
                Confirm password
              </label>
              <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                placeholder="Re-enter your password" autoComplete="new-password" required style={input} />
            </div>

            {needsMfa && (
              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, marginBottom: '6px' }}>
                  Authenticator code
                </label>
                <input
                  type="text" inputMode="numeric" autoComplete="one-time-code" maxLength={6}
                  value={mfaCode} onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="123456"
                  style={{ ...input, letterSpacing: '0.3em', textAlign: 'center', fontSize: '18px' }}
                />
                <p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '6px' }}>
                  This account uses two-factor authentication. Enter the 6-digit code from your authenticator app to confirm.
                </p>
              </div>
            )}

            {error && (
              <div style={{
                padding: '10px 12px', borderRadius: '6px', background: '#FEE2E2',
                color: '#DC2626', fontSize: '13px', marginBottom: '16px',
              }}>
                {error}
              </div>
            )}

            <button type="submit" disabled={saving} style={{
              width: '100%', padding: '11px', borderRadius: '8px',
              background: saving ? 'var(--accent-muted)' : 'var(--accent)',
              color: 'white', fontSize: '14px', fontWeight: 600,
              border: 'none', cursor: saving ? 'not-allowed' : 'pointer',
            }}>
              {saving ? 'Saving…' : 'Set password & continue'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
